import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PrototypeDto } from '@aiai/shared';
import { setPrototypeStorage } from '../api';
import { invalidatePrototype } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

/**
 * Toggle per-prototype S3 object storage. Enabling provisions a private MinIO
 * bucket and scoped credentials; disabling revokes them. The serve container
 * receives the S3_* env vars on the next redeploy after toggling.
 */
export function PrototypeStorageCard({ prototype }: { prototype: PrototypeDto }) {
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => setPrototypeStorage(prototype.id, enabled),
    onSuccess: () => {
      invalidatePrototype(queryClient, prototype.id);
    },
  });

  const bucketName = `proto-${prototype.deployment.slug}`;

  return (
    <SectionCard title="Object storage">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            id="prototype-storage-enabled"
            type="checkbox"
            className="checkbox"
            checked={prototype.storageEnabled}
            onChange={(e) => toggleMutation.mutate(e.target.checked)}
            disabled={toggleMutation.isPending}
          />
          <label htmlFor="prototype-storage-enabled" className="text-sm text-ink-muted">
            Enable S3 object storage (MinIO) for this prototype
          </label>
          {toggleMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
        </div>

        <p className="text-xs text-ink-faint">
          Toggling provisions or revokes the bucket and scoped credentials. The serve
          container receives the <span className="font-mono">S3_*</span> env vars on its
          next redeploy — use <strong>Redeploy</strong> from the deploy menu to apply
          immediately.
        </p>

        {prototype.storageEnabled && (
          <div className="space-y-3 border-t border-line pt-3">
            <div className="rounded-md bg-surface-raised px-3 py-2.5 space-y-1.5">
              <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
                Storage details (read-only)
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
                <span className="text-ink-faint select-none">S3_BUCKET</span>
                <span className="text-ink break-all">{bucketName}</span>
                <span className="text-ink-faint select-none">S3_ENDPOINT</span>
                <span className="text-ink break-all">
                  {prototype.storageEndpoint ?? (
                    <span className="text-amber-500">not configured</span>
                  )}
                </span>
              </div>
            </div>
            <p className="text-xs text-ink-faint">
              The secret key is injected directly into the serve container and is never
              displayed here.
            </p>
          </div>
        )}

        {toggleMutation.isError && (
          <Callout tone="danger">
            {errorMessage(toggleMutation.error, 'Storage action failed')}
          </Callout>
        )}
      </div>
    </SectionCard>
  );
}
